import { useMemo, useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { useNavigate, useSearchParams } from "react-router-dom";
import { usePostHog } from "@posthog/react";
import BusinessListingsMarketplaceTable from "../../components/business-listings/BusinessListingsMarketplaceTable";
import Toast, { type ToastTone } from "../../components/ui/Toast/Toast";
import GigsTable from "../../components/gigs";
import RecentlyCompletedGigsModal from "../../components/gigs/RecentlyCompletedGigsModal";
import { BoardTypeToggle, type BoardTypeFilter, type DistanceFilter, type PaySortOption, type TimeFrameFilter } from "../../components/gigs/TableFilters";
import Button from "../../components/ui/Button/Button";
import Modal from "../../components/ui/Modal/Modal";
import useTeenGuardianRequirement from "../../hooks/useTeenGuardianRequirement";
import { useAuth } from "../../hooks/useAuth";
import { captureBusinessListingApplied, captureGigApplied } from "../../lib/analytics";
import { businessListingsApi } from "../../lib/businessListings";
import { useDebouncedValue } from "../../hooks/useDebouncedValue";
import { gigsApi } from "../../lib/gigs";
import { stripeApi } from "../../lib/stripe";
import type { BusinessListingApplicationRequest } from "../../types/businessListingTypes";
import type { GigDetailResponse, GigListItemResponse, GigListRequest, GigRequestRequest, PropertyPredicateDto } from "../../types/gigTypes";
import type { StripePayoutProfileResponse } from "../../types/stripeTypes";
import { hasRole } from "../../utils/AuthUtils";
import { apiErrorToMessage } from "../../utils/ApiUtils";
import { addDays, endOfLocalDay, parseLocalDateInput, startOfLocalDay, toIsoTimestamp } from "../../utils/DateUtils";
import { buildGuardianSetupHref } from "../../utils/GuardianSetupUtils";

const DEFAULT_PAGE_SIZE = 25;
type ToastState = { message: string; tone: ToastTone } | null;

const resolveInitialBoardFilter = (
  boardParam: string | null,
  deepLinkedBusinessListingId: string | null,
  deepLinkedGigId: string | null,
): BoardTypeFilter | null => {
  if (boardParam === "VOLUNTEER_GIGS") {
    return "VOLUNTEER_GIGS";
  }
  if (deepLinkedBusinessListingId || boardParam === "BUSINESS_LISTINGS") {
    return "BUSINESS_LISTINGS";
  }
  if (boardParam === "PAID_GIGS") {
    return "PAID_GIGS";
  }
  return deepLinkedGigId ? "PAID_GIGS" : null;
};

const getGigSortBy = (paySort: PaySortOption) => {
  if (paySort === "PAY_DESC") {
    return [{ direction: "DESC" as const, propertyName: "paymentAmountCents" }];
  }
  if (paySort === "PAY_ASC") {
    return [{ direction: "ASC" as const, propertyName: "paymentAmountCents" }];
  }
  return [{ direction: "DESC" as const, propertyName: "createdAt" }];
};

const getBusinessListingSortAmountCents = (
  listing: { minPayAmountCents: number; maxPayAmountCents: number },
  paySort: PaySortOption,
) => paySort === "PAY_ASC" ? listing.minPayAmountCents : listing.maxPayAmountCents;

const isTeenPayoutReady = (profile: StripePayoutProfileResponse | null | undefined) =>
  profile?.onboardingStatus === "READY" && profile.payoutsEnabled;

const sortBusinessListingsByPay = <T extends { minPayAmountCents: number; maxPayAmountCents: number }>(
  listings: T[],
  paySort: PaySortOption,
) => {
  if (paySort === "DEFAULT") {
    return listings;
  }

  return [...listings].sort((left, right) => {
    const leftAmount = getBusinessListingSortAmountCents(left, paySort);
    const rightAmount = getBusinessListingSortAmountCents(right, paySort);
    return paySort === "PAY_DESC" ? rightAmount - leftAmount : leftAmount - rightAmount;
  });
};

const pinCurrentUserListings = <T extends { posterUserId: string | null }>(
  listings: T[],
  currentUserId?: string | null,
) => {
  if (!currentUserId) {
    return listings;
  }

  const currentUserListings: T[] = [];
  const otherListings: T[] = [];
  listings.forEach((listing) => {
    if (listing.posterUserId === currentUserId) {
      currentUserListings.push(listing);
    } else {
      otherListings.push(listing);
    }
  });

  return currentUserListings.length > 0 ? [...currentUserListings, ...otherListings] : listings;
};

const toGigListItem = (gig: GigDetailResponse): GigListItemResponse => ({
  id: gig.id,
  posterUserId: gig.posterUserId,
  posterFirstName: null,
  posterIdentityVerified: gig.posterIdentityVerified,
  posterRating: null,
  distanceMiles: null,
  title: gig.title,
  description: gig.description,
  requiresEquipment: gig.requiresEquipment,
  equipmentDescription: gig.equipmentDescription,
  category: gig.category,
  images: gig.images ?? [],
  locationType: gig.locationType,
  city: gig.city,
  state: gig.state,
  zip: gig.zip,
  timeZone: gig.timeZone,
  paymentAmountCents: gig.paymentAmountCents,
  volunteer: gig.volunteer,
  communityServiceHoursEligible: gig.communityServiceHoursEligible,
  communityServiceProofDetails: gig.communityServiceProofDetails,
  estimatedDurationMinutes: gig.estimatedDurationMinutes,
  status: gig.status,
  startTime: gig.startTime,
  endTime: gig.endTime,
  scheduleType: gig.scheduleType,
  scheduleNotes: gig.scheduleNotes,
  preferredStartTime: gig.preferredStartTime,
  preferredEndTime: gig.preferredEndTime,
  createdAt: gig.createdAt,
  fundingStatus: null,
  fundedOpen: false,
  teensRequired: gig.teensRequired,
  selectedSeatCount: gig.selectedSeatCount,
  confirmedSeatCount: gig.confirmedSeatCount,
  spotsRemaining: gig.spotsRemaining,
});

const GigsPage = () => {
  const posthog = usePostHog();
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const { user } = useAuth();
  const deepLinkedBusinessListingId = searchParams.get("listingId");
  const deepLinkedGigId = searchParams.get("gigId");
  const boardFilter = resolveInitialBoardFilter(searchParams.get("board"), deepLinkedBusinessListingId, deepLinkedGigId);
  const [search, setSearch] = useState("");
  const [timeFrame, setTimeFrame] = useState<TimeFrameFilter>("ANY");
  const [distance, setDistance] = useState<DistanceFilter>("ANY");
  const [customFromDate, setCustomFromDate] = useState("");
  const [customToDate, setCustomToDate] = useState("");
  const [paySort, setPaySort] = useState<PaySortOption>("DEFAULT");
  const [page, setPage] = useState(0);
  const [businessSearch, setBusinessSearch] = useState("");
  const [businessPaySort, setBusinessPaySort] = useState<PaySortOption>("DEFAULT");
  const [businessPage, setBusinessPage] = useState(0);
  const [volunteerBusinessPage, setVolunteerBusinessPage] = useState(0);
  const [businessDeepLinkDismissed, setBusinessDeepLinkDismissed] = useState(false);
  const [toast, setToast] = useState<ToastState>(null);
  const [isGuardianSetupModalOpen, setIsGuardianSetupModalOpen] = useState(false);
  const [stripePayoutSetupRequestId, setStripePayoutSetupRequestId] = useState<string | null>(null);
  const [successfulGigRequestId, setSuccessfulGigRequestId] = useState<string | null>(null);
  const debouncedSearch = useDebouncedValue(search, 300);
  const debouncedBusinessSearch = useDebouncedValue(businessSearch, 300);
  const canRequestAsTeen = hasRole(user?.roles, "TEEN");
  const teenGuardianRequirement = useTeenGuardianRequirement();
  const isGigBoard = boardFilter === "PAID_GIGS";
  const businessMarketplaceSearch = boardFilter === "BUSINESS_LISTINGS" ? debouncedBusinessSearch : debouncedSearch;

  const requestPredicates: PropertyPredicateDto[] = [
    { propertyName: "status", propertyValue: "PUBLISHED", operation: "EQUALS" },
    { propertyName: "volunteer", propertyValue: "false", operation: "EQUALS" },
  ];
  if (debouncedSearch.trim().length > 0) {
    requestPredicates.push({ propertyName: "search", propertyValue: debouncedSearch.trim(), operation: "CONTAINS" });
  }
  if (distance !== "ANY") {
    requestPredicates.push({ propertyName: "radiusMiles", propertyValue: distance, operation: "EQUALS" });
  }

  const now = new Date();
  if (timeFrame === "FLEXIBLE") {
    requestPredicates.push({ propertyName: "scheduleType", propertyValue: "FLEXIBLE", operation: "EQUALS" });
  } else if (timeFrame === "TODAY") {
    requestPredicates.push({
      propertyName: "startTime",
      propertyValue: toIsoTimestamp(startOfLocalDay(now)),
      operation: "TIMESTAMP_FROM",
    });
    requestPredicates.push({
      propertyName: "startTime",
      propertyValue: toIsoTimestamp(endOfLocalDay(now)),
      operation: "TIMESTAMP_TO",
    });
  } else if (timeFrame === "NEXT_7_DAYS") {
    const from = startOfLocalDay(now);
    const to = endOfLocalDay(addDays(now, 7));
    requestPredicates.push({
      propertyName: "startTime",
      propertyValue: toIsoTimestamp(from),
      operation: "TIMESTAMP_FROM",
    });
    requestPredicates.push({
      propertyName: "startTime",
      propertyValue: toIsoTimestamp(to),
      operation: "TIMESTAMP_TO",
    });
  } else if (timeFrame === "THIS_WEEKEND") {
    const day = now.getDay();
    const saturday = day === 6 ? now : day === 0 ? addDays(now, -1) : addDays(now, 6 - day);
    const sunday = day === 6 ? addDays(now, 1) : day === 0 ? now : addDays(now, 7 - day);
    requestPredicates.push({
      propertyName: "startTime",
      propertyValue: toIsoTimestamp(startOfLocalDay(saturday)),
      operation: "TIMESTAMP_FROM",
    });
    requestPredicates.push({
      propertyName: "startTime",
      propertyValue: toIsoTimestamp(endOfLocalDay(sunday)),
      operation: "TIMESTAMP_TO",
    });
  } else if (timeFrame === "CUSTOM") {
    const from = parseLocalDateInput(customFromDate);
    const to = parseLocalDateInput(customToDate);
    if (from) {
      requestPredicates.push({
        propertyName: "startTime",
        propertyValue: toIsoTimestamp(startOfLocalDay(from)),
        operation: "TIMESTAMP_FROM",
      });
    }
    if (to) {
      requestPredicates.push({
        propertyName: "startTime",
        propertyValue: toIsoTimestamp(endOfLocalDay(to)),
        operation: "TIMESTAMP_TO",
      });
    }
  }

  const requestPayload: GigListRequest = {
    predicates: requestPredicates,
    page: {
      number: page,
      size: DEFAULT_PAGE_SIZE,
      numberOfElements: null,
      hasNext: false,
      hasPrevious: false,
      first: false,
      last: false,
      sortBy: getGigSortBy(paySort),
    },
  };

  const gigsQuery = useQuery({
    queryKey: [
      "gigs",
      {
        page,
        pageSize: DEFAULT_PAGE_SIZE,
        paySort,
        debouncedSearch,
        onlyOpen: true,
        timeFrame,
        distance,
        customFromDate,
        customToDate,
      },
    ],
    queryFn: () => gigsApi.list(requestPayload),
    enabled: isGigBoard,
    placeholderData: (previousData) => previousData,
  });

  const listedGigs = gigsQuery.data?.gigs ?? [];
  const deepLinkedGigInList = deepLinkedGigId
    ? listedGigs.some((gig) => gig.id === deepLinkedGigId)
    : false;
  const deepLinkedGigQuery = useQuery({
    queryKey: ["gig-detail", deepLinkedGigId],
    queryFn: () => gigsApi.getById(deepLinkedGigId!),
    enabled: isGigBoard && Boolean(deepLinkedGigId) && !deepLinkedGigInList,
    retry: false,
  });
  const deepLinkedGig = deepLinkedGigQuery.data ? toGigListItem(deepLinkedGigQuery.data) : null;

  const businessListingsQuery = useQuery({
    queryKey: ["business-listings-marketplace", { businessMarketplaceSearch, distance }],
    queryFn: () => businessListingsApi.listMarketplace(businessMarketplaceSearch, distance),
    enabled: boardFilter === "BUSINESS_LISTINGS" || boardFilter === "VOLUNTEER_GIGS",
    placeholderData: (previousData) => previousData,
  });

  const recentlyCompletedQuery = useQuery({
    queryKey: ["recently-completed-gigs"],
    queryFn: () => gigsApi.listRecentlyCompleted(),
    staleTime: 5 * 60 * 1000,
    enabled: boardFilter !== null,
  });
  const teenPayoutProfileQuery = useQuery({
    queryKey: ["teen-payout-profile"],
    queryFn: () => stripeApi.getTeenPayoutProfile(),
    enabled: canRequestAsTeen && isGigBoard,
  });

  const openGuardianSetupModal = () => {
    setToast(null);
    setIsGuardianSetupModalOpen(true);
  };

  const goToGuardianSetup = () => {
    setIsGuardianSetupModalOpen(false);
    navigate(buildGuardianSetupHref("/gigs"));
  };

  const openStripePayoutSetupModal = (requestId: string) => {
    setToast(null);
    setStripePayoutSetupRequestId(requestId);
  };

  const openGigApplicationSuccessModal = (requestId: string) => {
    setToast(null);
    setSuccessfulGigRequestId(requestId);
  };

  const closeGigApplicationSuccessModal = () => {
    setSuccessfulGigRequestId(null);
  };

  const goToAppliedGig = () => {
    const requestId = successfulGigRequestId;
    setSuccessfulGigRequestId(null);
    navigate(requestId ? `/teen/gigs?requestId=${encodeURIComponent(requestId)}` : "/teen/gigs");
  };

  const goToAppliedGigAndSetUpStripeLater = () => {
    const requestId = stripePayoutSetupRequestId;
    setStripePayoutSetupRequestId(null);
    navigate(requestId ? `/teen/gigs?requestId=${encodeURIComponent(requestId)}` : "/teen/gigs");
  };

  const goToStripePayoutSetup = () => {
    setStripePayoutSetupRequestId(null);
    navigate("/settings?section=gig-payouts");
  };

  const onRequestGig = async (gigId: string, payload: GigRequestRequest) => {
    try {
      const request = await gigsApi.requestAsTeen(gigId, payload);
      await gigsQuery.refetch();
      captureGigApplied(posthog, { gig_id: gigId, source: "gig_board" });
      const requestedGig = listedGigs.find((gig) => gig.id === gigId)
        ?? (deepLinkedGig?.id === gigId ? deepLinkedGig : null);
      if (requestedGig && !requestedGig.volunteer && requestedGig.paymentAmountCents !== null) {
        const payoutProfile = teenPayoutProfileQuery.data ?? (await teenPayoutProfileQuery.refetch()).data;
        if (isTeenPayoutReady(payoutProfile)) {
          openGigApplicationSuccessModal(request.id);
        } else {
          openStripePayoutSetupModal(request.id);
        }
      } else {
        openGigApplicationSuccessModal(request.id);
      }
    } catch (requestError) {
      const message = apiErrorToMessage(requestError, "Unable to submit application.");
      if (message.includes("Add a parent or guardian before applying for gigs")) {
        openGuardianSetupModal();
        throw new Error(message);
      }
      setToast({ tone: "error", message });
      throw new Error(message);
    }
  };

  const onApplyToBusinessListing = async (listingId: string, payload: BusinessListingApplicationRequest) => {
    try {
      await businessListingsApi.applyAsTeen(listingId, payload);
      await businessListingsQuery.refetch();
      captureBusinessListingApplied(posthog, { listing_id: listingId, source: "business_listing_board" });
      setToast({ tone: "success", message: "Application submitted." });
    } catch (requestError) {
      const message = apiErrorToMessage(requestError, "Unable to submit application.");
      if (message.includes("Add a parent or guardian before applying for gigs")) {
        openGuardianSetupModal();
        throw new Error(message);
      }
      setToast({ tone: "error", message });
      throw new Error(message);
    }
  };

  const handleBeforeRequestGig = () => {
    if (!canRequestAsTeen) {
      return true;
    }

    if (!teenGuardianRequirement.requiresGuardian) {
      return true;
    }

    if (teenGuardianRequirement.isLoading || teenGuardianRequirement.isFetching) {
      setToast({ tone: "info", message: "Checking your guardian setup. Try again in a second." });
      return false;
    }

    if (teenGuardianRequirement.requiresSetup) {
      openGuardianSetupModal();
      return false;
    }

    return true;
  };

  const handleBeforeApplyToBusinessListing = () => {
    if (!canRequestAsTeen) {
      return true;
    }

    if (!teenGuardianRequirement.requiresGuardian) {
      return true;
    }

    if (teenGuardianRequirement.isLoading || teenGuardianRequirement.isFetching) {
      setToast({ tone: "info", message: "Checking your guardian setup. Try again in a second." });
      return false;
    }

    if (teenGuardianRequirement.requiresSetup) {
      openGuardianSetupModal();
      return false;
    }

    return true;
  };

  const onSearchChange = (value: string) => {
    setSearch(value);
    setPage(0);
    setVolunteerBusinessPage(0);
  };

  const onTimeFrameChange = (value: TimeFrameFilter) => {
    setTimeFrame(value);
    setPage(0);
  };

  const onDistanceChange = (value: DistanceFilter) => {
    setDistance(value);
    setPage(0);
    setBusinessPage(0);
    setVolunteerBusinessPage(0);
  };

  const onCustomFromDateChange = (value: string) => {
    setCustomFromDate(value);
    setPage(0);
  };

  const onCustomToDateChange = (value: string) => {
    setCustomToDate(value);
    setPage(0);
  };

  const onPaySortChange = (value: PaySortOption) => {
    setPaySort(value);
    setPage(0);
    setVolunteerBusinessPage(0);
  };

  const onBusinessSearchChange = (value: string) => {
    setBusinessSearch(value);
    setBusinessPage(0);
    setBusinessDeepLinkDismissed(true);
  };

  const onBusinessPaySortChange = (value: PaySortOption) => {
    setBusinessPaySort(value);
    setBusinessPage(0);
    setBusinessDeepLinkDismissed(true);
  };

  const onBusinessPageChange = (nextPage: number) => {
    setBusinessDeepLinkDismissed(true);
    setBusinessPage(nextPage);
  };

  const onBoardFilterChange = (value: BoardTypeFilter) => {
    setSearchParams((current) => {
      const next = new URLSearchParams(current);
      next.set("board", value);
      next.delete("gigId");
      next.delete("listingId");
      return next;
    });
    if (value === "BUSINESS_LISTINGS") {
      setBusinessPage(0);
    } else if (value === "VOLUNTEER_GIGS") {
      setPage(0);
      setVolunteerBusinessPage(0);
    } else {
      setPage(0);
    }
  };

  const sortedBusinessJobListings = useMemo(() => {
    const businessListings = businessListingsQuery.data ?? [];
    const sortedListings = sortBusinessListingsByPay(
      businessListings.filter((listing) => listing.jobType !== "VOLUNTEER"),
      businessPaySort,
    );
    return pinCurrentUserListings(sortedListings, user?.id);
  }, [businessListingsQuery.data, businessPaySort, user?.id]);

  const sortedVolunteerBusinessListings = useMemo(() => {
    const businessListings = businessListingsQuery.data ?? [];
    const sortedListings = sortBusinessListingsByPay(
      businessListings.filter((listing) => listing.jobType === "VOLUNTEER"),
      paySort,
    );
    return pinCurrentUserListings(sortedListings, user?.id);
  }, [businessListingsQuery.data, paySort, user?.id]);

  const deepLinkedBusinessPage = useMemo(() => {
    if (businessDeepLinkDismissed || !deepLinkedBusinessListingId || sortedBusinessJobListings.length === 0) {
      return null;
    }
    const listingIndex = sortedBusinessJobListings.findIndex((listing) => listing.id === deepLinkedBusinessListingId);
    return listingIndex >= 0 ? Math.floor(listingIndex / DEFAULT_PAGE_SIZE) : null;
  }, [businessDeepLinkDismissed, deepLinkedBusinessListingId, sortedBusinessJobListings]);

  const displayedBusinessPage = deepLinkedBusinessPage ?? businessPage;
  const businessStart = displayedBusinessPage * DEFAULT_PAGE_SIZE;
  const paginatedBusinessListings = sortedBusinessJobListings.slice(businessStart, businessStart + DEFAULT_PAGE_SIZE);
  const hasBusinessNextPage = businessStart + DEFAULT_PAGE_SIZE < sortedBusinessJobListings.length;
  const hasBusinessPreviousPage = displayedBusinessPage > 0;
  const volunteerBusinessStart = volunteerBusinessPage * DEFAULT_PAGE_SIZE;
  const paginatedVolunteerBusinessListings = sortedVolunteerBusinessListings.slice(
    volunteerBusinessStart,
    volunteerBusinessStart + DEFAULT_PAGE_SIZE,
  );
  const hasVolunteerBusinessNextPage = volunteerBusinessStart + DEFAULT_PAGE_SIZE < sortedVolunteerBusinessListings.length;
  const hasVolunteerBusinessPreviousPage = volunteerBusinessPage > 0;

  const error = gigsQuery.error ? apiErrorToMessage(gigsQuery.error, "Failed to load gigs.") : null;
  const businessError = businessListingsQuery.error
    ? apiErrorToMessage(businessListingsQuery.error, "Failed to load business listings.")
    : null;
  const hasPendingGuardianSetupRequest = teenGuardianRequirement.requiresGuardian
    && !teenGuardianRequirement.hasActiveGuardian
    && teenGuardianRequirement.pendingGuardianCount > 0;
  const guardianSetupModalTitle = hasPendingGuardianSetupRequest
    ? "Parent/guardian request pending"
    : "Add a parent/guardian before applying";
  const guardianSetupModalBody = hasPendingGuardianSetupRequest
    ? "Your parent/guardian request is still pending. They need to accept the connection before you can apply to gigs."
    : "Teens under 18 need an active parent or guardian connected before applying to gigs. Add or request one, then come back to apply.";
  const guardianSetupModalActionLabel = hasPendingGuardianSetupRequest ? "View Guardian Requests" : "Add Parent/Guardian";

  return (
    <main className="mx-auto max-w-4xl px-2 pb-12 pt-2 sm:px-6 sm:pt-10 md:py-16">
      <Toast
        isOpen={toast !== null}
        tone={toast?.tone ?? "info"}
        message={toast?.message ?? ""}
        onClose={() => setToast(null)}
      />
      {boardFilter === null ? (
        <BoardTypeToggle id="gig-board-type" value={null} onChange={onBoardFilterChange} layout="chooser" />
      ) : (
        <section className="mx-[calc(50%_-_50vw)] mt-3 w-screen px-2 sm:mx-0 sm:mt-5 sm:w-auto sm:px-0">
          <div className="grid gap-3 lg:grid-cols-[minmax(0,1fr)_auto] lg:items-stretch">
            <div className="order-2 lg:order-1">
              <BoardTypeToggle id="gig-board-type" value={boardFilter} onChange={onBoardFilterChange} />
            </div>
            <div className="order-1 flex rounded-2xl border border-myteengig-border bg-white p-3 shadow-sm sm:p-4 lg:order-2 lg:min-w-[230px]">
              <div className="flex min-w-0 flex-1 flex-col justify-between gap-3">
                <p className="text-sm font-semibold text-myteengig-text-primary">Completed work</p>
                <RecentlyCompletedGigsModal
                  data={recentlyCompletedQuery.data}
                  isLoading={recentlyCompletedQuery.isLoading}
                />
              </div>
            </div>
          </div>
        </section>
      )}

      {isGigBoard && error ? <p className="mt-4 text-sm font-medium text-red-600">{error}</p> : null}
      {boardFilter === "VOLUNTEER_GIGS" && businessError ? <p className="mt-4 text-sm font-medium text-red-600">{businessError}</p> : null}
      {boardFilter === "BUSINESS_LISTINGS" && businessError ? <p className="mt-4 text-sm font-medium text-red-600">{businessError}</p> : null}

      {boardFilter === "PAID_GIGS" && !error ? (
        <GigsTable
          gigs={listedGigs}
          autoExpandGigId={deepLinkedGigId}
          currentUserId={user?.id ?? null}
          currentUserZip={user?.zip ?? null}
          canRequestAsTeen={canRequestAsTeen}
          isLoading={gigsQuery.isLoading || gigsQuery.isFetching}
          search={search}
          timeFrame={timeFrame}
          distance={distance}
          customFromDate={customFromDate}
          customToDate={customToDate}
          page={page}
          pageSize={DEFAULT_PAGE_SIZE}
          paySort={paySort}
          resultsCount={gigsQuery.data?.page.numberOfElements ?? gigsQuery.data?.gigs.length ?? 0}
          hasNextPage={Boolean(gigsQuery.data?.page.hasNext)}
          hasPreviousPage={Boolean(gigsQuery.data?.page.hasPrevious)}
          onSearchChange={onSearchChange}
          onTimeFrameChange={onTimeFrameChange}
          onDistanceChange={onDistanceChange}
          onCustomFromDateChange={onCustomFromDateChange}
          onCustomToDateChange={onCustomToDateChange}
          onPaySortChange={onPaySortChange}
          onPageChange={setPage}
          onRequestGig={onRequestGig}
          onBeforeRequestGig={handleBeforeRequestGig}
          deepLinkedGig={deepLinkedGig}
          emptyMessage="No homeowner gigs found."
        />
      ) : null}

      {boardFilter === "VOLUNTEER_GIGS" ? (
        !businessError ? (
          <BusinessListingsMarketplaceTable
            listings={paginatedVolunteerBusinessListings}
            autoExpandListingId={deepLinkedBusinessListingId}
            canApplyAsTeen={canRequestAsTeen}
            currentUserId={user?.id ?? null}
            currentUserZip={user?.zip ?? null}
            isLoading={businessListingsQuery.isLoading || businessListingsQuery.isFetching}
            search={search}
            distance={distance}
            paySort={paySort}
            page={volunteerBusinessPage}
            pageSize={DEFAULT_PAGE_SIZE}
            resultsCount={paginatedVolunteerBusinessListings.length}
            hasNextPage={hasVolunteerBusinessNextPage}
            hasPreviousPage={hasVolunteerBusinessPreviousPage}
            onSearchChange={onSearchChange}
            onDistanceChange={onDistanceChange}
            onPaySortChange={onPaySortChange}
            onPageChange={setVolunteerBusinessPage}
            onApply={onApplyToBusinessListing}
            onBeforeApply={handleBeforeApplyToBusinessListing}
            emptyMessage="No volunteer business postings found."
          />
        ) : null
      ) : null}

      {boardFilter === "BUSINESS_LISTINGS" && !businessError ? (
        <BusinessListingsMarketplaceTable
          listings={paginatedBusinessListings}
          autoExpandListingId={deepLinkedBusinessListingId}
          canApplyAsTeen={canRequestAsTeen}
          currentUserId={user?.id ?? null}
          currentUserZip={user?.zip ?? null}
          isLoading={businessListingsQuery.isLoading || businessListingsQuery.isFetching}
          search={businessSearch}
          distance={distance}
          paySort={businessPaySort}
          page={displayedBusinessPage}
          pageSize={DEFAULT_PAGE_SIZE}
          resultsCount={paginatedBusinessListings.length}
          hasNextPage={hasBusinessNextPage}
          hasPreviousPage={hasBusinessPreviousPage}
          onSearchChange={onBusinessSearchChange}
          onDistanceChange={onDistanceChange}
          onPaySortChange={onBusinessPaySortChange}
          onPageChange={onBusinessPageChange}
          onApply={onApplyToBusinessListing}
          onBeforeApply={handleBeforeApplyToBusinessListing}
        />
      ) : null}

      <Modal
        isOpen={isGuardianSetupModalOpen}
        onClose={() => setIsGuardianSetupModalOpen(false)}
        title={guardianSetupModalTitle}
        panelClassName="max-w-lg"
      >
        <div className="space-y-5">
          <p className="text-sm leading-6 text-myteengig-text-muted">
            {guardianSetupModalBody}
          </p>
          <div className="flex flex-col-reverse gap-2 sm:flex-row sm:justify-end">
            <Button type="button" variant="outline" onClick={() => setIsGuardianSetupModalOpen(false)}>
              Not Now
            </Button>
            <Button type="button" onClick={goToGuardianSetup}>
              {guardianSetupModalActionLabel}
            </Button>
          </div>
        </div>
      </Modal>
      <Modal
        isOpen={successfulGigRequestId !== null}
        onClose={closeGigApplicationSuccessModal}
        title="Application submitted"
        panelClassName="max-w-lg"
      >
        <div className="space-y-5">
          <div className="rounded-xl border border-emerald-200 bg-emerald-50 p-4">
            <div className="flex gap-3">
              <span className="flex h-10 w-10 shrink-0 items-center justify-center rounded-full bg-emerald-600 text-white">
                <svg aria-hidden="true" className="h-5 w-5" viewBox="0 0 20 20" fill="none">
                  <path
                    d="M16.25 5.75 8.5 13.5 4.75 9.75"
                    stroke="currentColor"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth="2"
                  />
                </svg>
              </span>
              <div className="min-w-0">
                <p className="text-sm font-semibold text-emerald-950">Your application was submitted.</p>
                <p className="mt-2 text-sm leading-6 text-emerald-900">
                  Go to <strong className="font-semibold text-emerald-950">Gigs Applied For</strong> to see this gig.
                </p>
              </div>
            </div>
          </div>
          <div className="flex flex-col-reverse gap-2 sm:flex-row sm:justify-end">
            <Button type="button" variant="outline" onClick={closeGigApplicationSuccessModal}>
              Close
            </Button>
            <Button type="button" onClick={goToAppliedGig}>
              View Gigs Applied For
            </Button>
          </div>
        </div>
      </Modal>
      <Modal
        isOpen={stripePayoutSetupRequestId !== null}
        onClose={() => setStripePayoutSetupRequestId(null)}
        title="Application submitted"
        panelClassName="max-w-lg"
      >
        <div className="space-y-5">
          <div className="rounded-xl border border-emerald-200 bg-emerald-50 p-4">
            <div className="flex gap-3">
              <span className="flex h-10 w-10 shrink-0 items-center justify-center rounded-full bg-emerald-600 text-white">
                <svg aria-hidden="true" className="h-5 w-5" viewBox="0 0 20 20" fill="none">
                  <path
                    d="M16.25 5.75 8.5 13.5 4.75 9.75"
                    stroke="currentColor"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth="2"
                  />
                </svg>
              </span>
              <div className="min-w-0">
                <p className="text-sm font-semibold text-emerald-950">Your application was submitted.</p>
                <p className="mt-2 text-sm leading-6 text-emerald-900">
                  <strong className="font-semibold text-emerald-950">
                    Set up Stripe and add a bank account before you can be fully accepted for this gig.
                  </strong>{" "}
                  This lets the poster fund and confirm the gig if they select you, and lets MyTeenGig deliver the
                  poster's payment to your bank account after you complete the gig.
                </p>
              </div>
            </div>
          </div>
          <div className="flex flex-col-reverse gap-2 sm:flex-row sm:justify-end">
            <Button type="button" variant="outline" onClick={goToAppliedGigAndSetUpStripeLater}>
              View application and set up Stripe later.
            </Button>
            <Button type="button" onClick={goToStripePayoutSetup}>
              Complete Stripe Setup
            </Button>
          </div>
        </div>
      </Modal>
    </main>
  );
};

export default GigsPage;
